import { Box, Typography } from '@mui/material'
import { DeleteActionButton, EditActionButton, ViewActionButton } from '../../../components/MRTDataGrid/TableActionButtons'
import { MRT_ColumnDef as MRTColumnDef } from 'material-react-table'
import React, { memo, useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from '../../../app/hooks'
import { useNotificationStack } from '../../../app/hooks/useNotificationStack'
import { useConfirmDialogContext } from '../../../components/ConfirmDialog'
import { useDeleteProductCategoryMutation } from '../../../app/redux-fetch/apiProducts'
import { useGetCustomersQuery } from '../../../app/redux-fetch/apiQuery'
import { IDropDownItem } from '../../../app/types'
import AddButton from '../../../components/Buttons/AddButton'
import ConditionalRender from '../../../components/ConditionalRender/ConditionalRender'
import FormDropdown from '../../../components/FormDropDown/FormDropdown'
import MRTDataGridv2 from '../../../components/MRTDataGrid/MRTDataGridv2'
import YesNoTypography from '../../../components/YesNoTypograpphy/YesNoTypography'
import SideModalWrapper from '../../Assets/modals/SideModalWrapper'
import { fetchLoggedInUserSelector } from '../../selectors'
import { ILoggedInUser } from '../../types'
import { useProductTabContext } from '../ProductTabContext'
import { IProductCategory } from '../types'
import CreateProductCategoryModal from './CreateProductCategoryModal'
import { useProductCategory } from './hooks/useProductCategory'
import messages from './messages'
import ProductCategoryModal from './ProductCategoryModal'

interface ModalState {
  create: boolean
  edit: boolean
  view: boolean
}

const ProductCategoryTable: React.FC = memo(() => {
  const { formatMessage } = useIntl()
  const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)
  const isCustomerUser = loggedInUser.customerId !== undefined
  const tenantId = loggedInUser.tenantId ?? -1

  const { customerId: sharedCustomerId, setCustomerId } = useProductTabContext()
  const { data: customers = [] } = useGetCustomersQuery(tenantId)

  const { productCategories, isLoading } = useProductCategory({
    customerId: sharedCustomerId,
  })
  const [deleteProductCategory] = useDeleteProductCategoryMutation()
  const { enqueueSuccess, enqueueFailure } = useNotificationStack()
  const { confirm } = useConfirmDialogContext()

  const [modalState, setModalState] = useState<ModalState>({
    create: false,
    edit: false,
    view: false,
  })
  const [selectedProductCategoryId, setSelectedProductCategoryId] = useState(-1)

  const columns = useMemo<MRTColumnDef<IProductCategory>[]>(() => [
    {
      accessorKey: 'name',
      Cell: ({ cell }: any) => (
        <Typography sx={{ fontWeight: 700 }}>
          {cell.getValue()}
        </Typography>
      ),
      header: formatMessage(messages.name),
    },
    {
      accessorFn: (row) => {
        const code = row.hsSubheading?.subheadingCode ?? ''
        if (!code) return ''
        return `${code.slice(0, -2)} ${code.slice(-2)}`
      },
      header: formatMessage(messages.hsCode),
    },
    {
      accessorFn: (row) => row.hsSubheading?.hsHeading?.description ?? '',
      header: formatMessage(messages.hsHeading),
    },
    {
      accessorFn: (row) => row.hsSubheading?.description ?? '',
      header: formatMessage(messages.hsSubheading),
    },
    {
      accessorFn: (row) => {
        return row.inUse ? 'true' : 'false'
      },
      filterVariant: 'checkbox',
      header: formatMessage(messages.inUse),
      muiTableHeadCellProps: {
        align: 'center',
      },
      muiTableBodyCellProps: {
        align: 'center',
      },
      Cell: ({ cell }: any) => {
        return <YesNoTypography isYes={cell.getValue() === 'true'} />
      },
    },
  ], [formatMessage])

  const handleModalToggle = useCallback((modalType: keyof ModalState, isOpen?: boolean) => {
    setModalState(prev => ({
      ...prev,
      [modalType]: isOpen ?? !prev[modalType],
    }))
  }, [])

  const handleEditClick = useCallback((productCategoryId: number) => {
    setSelectedProductCategoryId(productCategoryId)
    handleModalToggle('edit', true)
  }, [handleModalToggle])

  const handleViewClick = useCallback((productCategoryId: number) => {
    setSelectedProductCategoryId(productCategoryId)
    handleModalToggle('view', true)
  }, [handleModalToggle])

  const handleCreateClick = useCallback(() => {
    handleModalToggle('create', true)
  }, [handleModalToggle])

  const handleCloseModal = useCallback((modalType: keyof ModalState) => {
    handleModalToggle(modalType, false)
    if (modalType !== 'create') {
      setSelectedProductCategoryId(-1)
    }
  }, [handleModalToggle])

  const handleCustomerChange = useCallback((e: any, name: any, newValue: any) => {
    setCustomerId?.(newValue)
  }, [setCustomerId])

  const renderRowActions = useCallback(({ row }: any) => (
    <Box sx={{ display: 'flex' }}>
      <ViewActionButton
        onClick={() => handleViewClick(row.id)}
      />
      <EditActionButton
        onClick={() => handleEditClick(row.id)}
      />
      <ConditionalRender condition={row.original.inUse == false}>
        <DeleteActionButton
          onClick={async () => {
            const confirmed = await confirm({
              title: formatMessage(messages.deleteProductCategoryTitle),
              message: formatMessage(messages.deleteProductCategoryDescription),
              confirmText: formatMessage(messages.deleteBtn),
              cancelText: formatMessage(messages.cancelBtn),
              severity: 'warning',
              confirmButtonColor: 'error',
              cancelButtonColor: 'secondary',
            })

            if (confirmed) {
              deleteProductCategory({ tenantId, customerId: sharedCustomerId, categoryId: row.id })
                .unwrap()
                .then(() => {
                  enqueueSuccess(messages.productCategoryDeleted)
                })
                .catch(() => {
                  enqueueFailure(messages.productCategoryNotDeleted)
                })
            }
          }}
        />
      </ConditionalRender>
    </Box>
  ), [handleEditClick, handleViewClick, deleteProductCategory, tenantId, sharedCustomerId, enqueueSuccess, enqueueFailure, confirm, formatMessage])

  const leftHeadingComponent = useMemo(() => {
    if (isCustomerUser) return <></>

    return (
      <FormDropdown
        id='customerId'
        items={customers as IDropDownItem[]}
        onChange={handleCustomerChange}
        sx={{ width: '400px', mt: '12px' }}
        value={sharedCustomerId}
        label='Customer'
      />
    )
  }, [isCustomerUser, customers, handleCustomerChange, sharedCustomerId])

  const rightHeadingComponent = useMemo(() => (
    <AddButton
      text={formatMessage(messages.newProductCategory)}
      onClick={handleCreateClick}
      disabled={!sharedCustomerId || sharedCustomerId < 1}
    />
  ), [formatMessage, handleCreateClick, sharedCustomerId])

  return (
    <>
      <SideModalWrapper
        open={modalState.create}
        key='create'
        headerLabel={formatMessage(messages.createEditProductCategory)}
        onClose={() => handleCloseModal('create')}
        withBottomBox={false}
      >
        <CreateProductCategoryModal customerId={sharedCustomerId} />
      </SideModalWrapper>

      <SideModalWrapper
        open={modalState.edit}
        key='edit'
        headerLabel={formatMessage(messages.createEditProductCategory)}
        onClose={() => handleCloseModal('edit')}
        withBottomBox={false}
      >
        <ProductCategoryModal productCategoryId={selectedProductCategoryId} />
      </SideModalWrapper>

      <SideModalWrapper
        open={modalState.view}
        key='view'
        headerLabel={formatMessage(messages.viewProductCategory)}
        onClose={() => handleCloseModal('view')}
        withBottomBox={false}
      >
        <ProductCategoryModal productCategoryId={selectedProductCategoryId} isDisabled />
      </SideModalWrapper>

      <MRTDataGridv2
        initialState={{
          pagination: { pageSize: 100, pageIndex: 0 },
          density: 'comfortable',
          sorting: [
            {
              id: 'name',
              desc: false,
            },
          ],
        }}
        state={{ isLoading }}
        leftHeadingComponent={leftHeadingComponent}
        rightHeadingComponent={rightHeadingComponent}
        columns={columns}
        data={productCategories}
        getRowId={(row) => row.id as any}
        enableEditing
        enableRowActions
        positionActionsColumn='last'
        renderRowActions={renderRowActions}
      />
    </>
  )
})

ProductCategoryTable.displayName = 'ProductCategoryTable'

export default ProductCategoryTable
